const DATABASE_NAME = 'havu-pending-uploads';
const STORE_NAME = 'zip-files';
const DATABASE_VERSION = 1;

interface StoredZipFile {
  blob: Blob;
  name: string;
  type: string;
  lastModified: number;
}

const openDatabase = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('The selected ZIP could not be saved.'));
  });
};

const runRequest = async <Result,>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<Result>
): Promise<Result> => {
  const database = await openDatabase();

  try {
    return await new Promise<Result>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = operation(transaction.objectStore(STORE_NAME));

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('The selected ZIP could not be saved.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('The selected ZIP could not be saved.'));
    });
  } finally {
    database.close();
  }
};

export const storePendingZip = async (file: File): Promise<string> => {
  const key = window.crypto.randomUUID();
  const value: StoredZipFile = {
    blob: file,
    name: file.name,
    type: file.type,
    lastModified: file.lastModified
  };

  await runRequest('readwrite', (store) => store.put(value, key));
  return key;
};

export const loadPendingZip = async (key: string): Promise<File | null> => {
  const stored = await runRequest<StoredZipFile | undefined>('readonly', (store) => store.get(key));

  return stored
    ? new File([stored.blob], stored.name, { type: stored.type, lastModified: stored.lastModified })
    : null;
};

export const deletePendingZip = async (key: string): Promise<void> => {
  await runRequest('readwrite', (store) => store.delete(key));
};
